import React from 'react';
import './Tabs.css';

// THE tab bar — the one the rest of the portal uses: icon, label, blue underline on the active tab
// (`.account-tabs` / `.tab-button` on the account, hotel, partner, vault and settings screens).
//
// Shared because that pattern had been hand-written on every screen that needed it, and the copies
// drifted: `.pp-tab` and `.tab-button` and `.tab-btn` — the last of those defined TWICE, in
// SyncProjectDetailsPage.css and VaultPricing.css, with different active colours. The portal ships one
// global CSS bundle, so those two are not two styles but a race decided by emit order. Anything new uses
// this and adds no further variant.
//
// Props
//   tabs      [{ key, label, icon?, count?, hidden? }] — `icon` is a 20px SVG element; a hidden tab is
//             not rendered at all (superadmin-only ones)
//   active    the selected key
//   onChange(key)
//   size      'md' (default, page-level) | 'sm' (inside a panel, where page-level padding out-shouts
//             the panel's own heading)
//   className extra classes on the bar
export default function Tabs({ tabs, active, onChange, size = 'md', className = '' }) {
    const shown = (tabs || []).filter(t => t && !t.hidden);
    if (!shown.length) return null;
    return (
        <div className={`hc-tabs${size === 'sm' ? ' hc-tabs-sm' : ''} ${className}`.trim()} role="tablist">
            {shown.map(t => (
                <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={active === t.key}
                    className={`hc-tab${active === t.key ? ' hc-tab-active' : ''}`}
                    onClick={() => onChange(t.key)}
                >
                    {t.icon}
                    <span className="hc-tab-label">{t.label}</span>
                    {/* A count belongs with its tab rather than inside the label string: it stays
                        legible when the tab is not selected, and the next caller cannot forget it. */}
                    {t.count !== undefined && t.count !== null && (
                        <span className="hc-tab-count">{t.count}</span>
                    )}
                </button>
            ))}
        </div>
    );
}

// The second level INSIDE one tab (`.hc-subtabs` above): two views of the same subject, quieter than the bar.
//
// Props
//   tabs      [{ key, label, superAdmin?, partnerOnly? }] — the flags only add the sa-only (purple) / partner-only
//             (teal) marker (STYLE.md); the caller filters who sees the tab
//   active    the selected key
//   onChange(key)
export function SubTabs({ tabs, active, onChange }) {
    if (!tabs || !tabs.length) return null;
    return (
        <div className="hc-subtabs" role="tablist">
            {tabs.map(({ key, label, superAdmin, partnerOnly }) => (
                <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={active === key}
                    className={`hc-subtab${active === key ? ' hc-subtab-active' : ''}${superAdmin ? ' sa-only' : ''}${partnerOnly ? ' partner-only' : ''}`}
                    onClick={() => onChange(key)}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}
